<script lang="ts">
	import {
		getEditionsGalleryCard,
		type EditionsGalleryCardEdition,
	} from '../../utils/getEditionsGalleryCard/getEditionsGalleryCard';
	import { LoadMoreInfinite } from '#lib/components/LoadMoreInfinite/index.js';
	import { ArtworkCard } from '$global/components/ArtworkCard';
	import { ArtworkCardLoading } from '$global/components/ArtworkCardLoading';
	import { CircularProgress } from '$global/components/CircularProgress';
	import { Container } from '$global/components/Container';
	import { Txt } from '$global/components/Txt';
	import { StickyFilterToolbar } from '$global/features/filter-drawer/components/Filter';
	import type {
		FilterSections,
		FilterSortOption,
	} from '$global/features/filter-drawer/types';
	import { trackSelectItem } from '$global/features/filter-drawer/utils/trackSelectItem/trackSelectItem';

	interface Props {
		dataCy: string;
		editions: EditionsGalleryCardEdition[] | undefined;
		filterSections: FilterSections;
		sortOptions: FilterSortOption[][];
		count?: number | undefined;
		showCount?: boolean;
		showSeriesOnlyWhenPreview?: boolean;
		// When true, an edition that belongs to a sub-series renders as a card for
		// that sub-series (its image, its parent's name, a link to the sub-series
		// page). Only meaningful with a query whose base filter returns one
		// stand-in edition per sub-series, as the gallery browse does; the series
		// page lists a sub-series' editions individually and leaves it off.
		showSubSeriesAsCards?: boolean;
		loading: boolean;
		loadingNextPage: boolean;
		hasMore: boolean;
		onLoadMore: () => void;
		// When true, the grid shows a "Load more" button first and only switches
		// to scroll-triggered loading once the button has been clicked.
		initialLoadMoreButton?: boolean;
		// Required so a new consumer cannot silently lose grid-interaction
		// tracking by forgetting to thread the prefix through.
		trackPrefix: string;
		// Names the grid in select_item events. Defaults to the track prefix so
		// existing consumers keep working, but pages rendering more than one
		// grid should pass a distinct value.
		listId?: string;
	}

	let {
		dataCy,
		editions,
		filterSections,
		sortOptions,
		count = undefined,
		showCount = true,
		showSeriesOnlyWhenPreview = false,
		showSubSeriesAsCards = false,
		loading,
		loadingNextPage,
		hasMore,
		onLoadMore,
		initialLoadMoreButton = false,
		trackPrefix,
		listId = undefined,
	}: Props = $props();

	let selectItemListId = $derived(listId || trackPrefix);
</script>

<div>
	<StickyFilterToolbar
		{dataCy}
		{filterSections}
		sorts={sortOptions}
		count={showCount ? count : undefined}
		{loading}
		{trackPrefix}
		resultsClass="mb-10"
	>
		{#snippet title()}
			<Txt variant="h3" component="h1">Editions</Txt>
		{/snippet}
	</StickyFilterToolbar>

	<Container {dataCy}>
		{#if loading}
			<div
				class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
			>
				{#each Array(12).fill(null)}
					<ArtworkCardLoading lines={3} {dataCy} />
				{/each}
			</div>
		{:else if editions && editions.length > 0}
			<div
				class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
			>
				{#each editions as edition, index}
					{@const card = getEditionsGalleryCard({
						edition,
						showSeriesOnlyWhenPreview,
						showSubSeriesAsCards,
					})}
					<ArtworkCard
						{dataCy}
						line1={card.line1}
						line2={card.line2}
						line3={card.line3}
						href={card.href}
						image={card.image}
						alt={card.alt}
						onClick={() =>
							trackSelectItem({
								itemId: card.itemId,
								position: index,
								listId: selectItemListId,
								artist: edition.artists,
							})}
					/>
				{/each}
			</div>

			<LoadMoreInfinite
				{dataCy}
				{hasMore}
				{onLoadMore}
				{initialLoadMoreButton}
				{trackPrefix}
				loadedCount={editions?.length || 0}
			/>

			{#if loadingNextPage}
				<div class="flex justify-center">
					<CircularProgress {dataCy} />
				</div>
			{/if}
		{:else}
			<div class="flex min-h-[50vh] flex-col justify-center p-2">
				<Txt variant="h2" class="mb-4 text-center">No editions found</Txt>

				<Txt class="text-center"
					>Please try a different search or widen the filters.</Txt
				>
			</div>
		{/if}
	</Container>
</div>
